import { bgColorIdToCssVar } from '@unifyapps/defs/constants/backgroundColorGroups';
import type { EffectColor, EffectsValue } from '@unifyapps/defs/types/effectObject';
import { EFFECT_TYPES } from '@unifyapps/defs/types/effectObject/constants';
import { hexToRgbaString } from '@unifyapps/ui/utils/contrastRatio';
import type { CSSProperties } from 'react';
import { getColorValue } from '../../../../components/ColorPicker/utils/getColorValue';
import { resolveEffectNumber } from './resolveEffectNumber';
import { getShadowAxes, type ShadowEffect } from './shadowAxes';

// Picker emits either a hex (custom) or a Tailwind class id (preset palette).
// The class id is not a valid CSS color — emitting it inside `box-shadow`
// invalidates the whole declaration, so it must be mapped to its CSS-var
// counterpart. Fallback is opaque black so a misconfigured row never silently
// poisons the rest of the shadow stack.
const resolveEffectColor = (color: EffectColor | undefined): string => {
  const computed = getColorValue(color);
  if (typeof computed !== 'string' || !computed) return 'rgba(0, 0, 0, 1)';
  if (computed.startsWith('#')) return hexToRgbaString(computed) ?? 'rgba(0, 0, 0, 1)';
  const cssVarName = bgColorIdToCssVar[computed];
  if (cssVarName) return `var(${cssVarName})`;
  if (/^(rgba?|hsla?)\(/.test(computed) || /^[a-zA-Z]+$/.test(computed)) return computed;
  return 'rgba(0, 0, 0, 1)';
};

const shadowToCss = (effect: ShadowEffect): string => {
  const axes = getShadowAxes(effect);
  const color = resolveEffectColor(axes.color);
  const inset = effect.type === EFFECT_TYPES.INNER_SHADOW ? 'inset ' : '';
  const x = resolveEffectNumber(axes.x);
  const y = resolveEffectNumber(axes.y);
  const blur = resolveEffectNumber(axes.blur);
  const spread = resolveEffectNumber(axes.spread);
  return `${inset}${x}px ${y}px ${blur}px ${spread}px ${color}`;
};

// `inner-shadow` and `background-blur` are intentionally NOT emitted here:
// - inner-shadow paints under children, so an opaque-bg child covers it.
//   Callers must mount an overlay sibling above children with the inset
//   `box-shadow`. See `getInnerShadowOverlayStyle`.
// - background-blur uses `backdrop-filter`, which only blurs through
//   transparent pixels — putting it on the same node that paints a fill makes
//   the blur invisible. See `getBackgroundBlurPx`.
export const getEffectsStyle = (effects: EffectsValue | undefined): CSSProperties => {
  if (!effects?.length) return {};

  const shadows: string[] = [];
  let layerBlur = 0;

  for (const effect of effects) {
    if (effect.visible === false) continue;
    switch (effect.type) {
      case EFFECT_TYPES.DROP_SHADOW:
        shadows.push(shadowToCss(effect));
        break;
      case EFFECT_TYPES.LAYER_BLUR:
        // Figma's blur sliders are 2× the CSS radius — halve symmetrically with
        // background-blur so a `blur: 84` row renders as `blur(42px)`.
        layerBlur += resolveEffectNumber(effect.blur) * 0.5;
        break;
    }
  }

  const result: CSSProperties = {};
  if (shadows.length > 0) result.boxShadow = shadows.join(', ');
  if (layerBlur > 0) result.filter = `blur(${layerBlur}px)`;
  return result;
};

export const getInnerShadowOverlayStyle = (
  effects: EffectsValue | undefined,
): CSSProperties | undefined => {
  if (!effects?.length) return undefined;
  const shadows: string[] = [];
  for (const effect of effects) {
    if (effect.visible === false) continue;
    if (effect.type === EFFECT_TYPES.INNER_SHADOW) shadows.push(shadowToCss(effect));
  }
  if (shadows.length === 0) return undefined;
  return { boxShadow: shadows.join(', ') };
};

export const getBackgroundBlurPx = (effects: EffectsValue | undefined): number => {
  if (!effects?.length) return 0;
  let backgroundBlur = 0;
  for (const effect of effects) {
    if (effect.visible === false) continue;
    // Same Figma 2× scale as layer-blur — halve so panel-to-render stays symmetric.
    if (effect.type === EFFECT_TYPES.BACKGROUND_BLUR) {
      backgroundBlur += resolveEffectNumber(effect.blur) * 0.5;
    }
  }
  return backgroundBlur;
};
